import { Box, ButtonBase, Card, CardContent, CardHeader, Divider, IconButton, Modal, Typography, useTheme } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { IFlow, INode } from '../../../definitions/Nodes'
import NodesIcon from '../nodes/NodesIcon'
import QuantumTimelineEngine, { IQuantumLayer, IQuantumPosition } from '../../../engines/QuantumTimelineEngine'
import { ChevronLeftRounded, ChevronRightRounded, CloseRounded, RouteRounded } from '@mui/icons-material'
import NodeManager from '../../../managers/NodeManager'

interface P {
    flow: IFlow
    open: boolean
    setOpen: (open: boolean) => void
    setError: (data: boolean) => void
}

const QuantumTimelineUI = ({ flow, open, setOpen, setError }: P) => {
    const [required, setRequired] = useState<string[]>([])

    const quMatrix = useMemo(() => {
        try {
            return QuantumTimelineEngine.simulate(flow)
        } catch (error) {
            console.error(error)
            return null
        }
    }, [flow])

    useEffect(() => {
        try {
            setError(null)
            QuantumTimelineEngine.simulate(flow)
        } catch (error) {
            setError(error)
        }
    }, [flow])

    return (
        <Modal open={open} onClose={() => setOpen(false)} disableEnforceFocus sx={{ display: 'flex', width: 'calc(100vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
            <Box margin='auto auto' width='60vw' height='80vh'>
                <Card variant='outlined' sx={{ height: '100%', padding: 2 }}>
                    <CardHeader
                        title={<Typography variant='h6'>Timeline</Typography>}
                        avatar={<RouteRounded />}
                        action={(
                            <IconButton onClick={() => setOpen(false)}>
                                <CloseRounded />
                            </IconButton>
                        )}
                    />
                    <CardContent sx={{ padding: 'var(--unit * 2)', height: '90%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                        {quMatrix?.layers.map(x => (
                            <QuantumLayer
                                key={x.depth}
                                layer={x}
                                required={required}
                                setRequired={setRequired}
                            />
                        ))}
                    </CardContent>
                </Card>
            </Box>
        </Modal>
    )
}

interface Pql {
    layer: IQuantumLayer
    required: string[]
    setRequired: (nodeIds: string[]) => void
}

const QuantumLayer = ({ layer, required, setRequired }: Pql) => {
    const { depth, superpositions } = layer
    const theme = useTheme()
    const [superPosIndex, setSuperPosIndex] = useState(0)
    const hasSuperpositions = superpositions.length > 1

    const renderNode = (node: INode) => {
        const description = NodeManager.getDescription(node.type)
        const isRequired = required.includes(node.uuid)
        return (
            <Box
                key={node.uuid}
                style={{
                    width: 150,
                    padding: 'var(--unit)',
                    border: '1px solid',
                    borderRadius: 'var(--unit)',
                    background: 'var(--bg-color)',
                    borderColor: isRequired ? 'var(--color-good)' : '#3c3c3c',
                    boxShadow: isRequired ? 'var(--xy-node-boxshadow-hover-default)' : '',
                    transition: 'all 0.3s'
                }}
                onMouseEnter={() => setRequired([node.uuid, ...node.inputs.map(x => x.uuid)])}
                onMouseLeave={() => setRequired([])}>
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'var(--unit)' }}>
                    <NodesIcon nodeType={node.type} size={18} />
                    <Box style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Typography fontSize={10} color={theme.palette.text.disabled} textTransform='uppercase' noWrap>
                            {description.name}
                        </Typography>
                        <Typography noWrap>{node.name}</Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    const renderSuperposition = (superposition: IQuantumPosition, index: number) => {
        const { nodes, measurement, prevSuperposition } = superposition
        const key = `${prevSuperposition ?? ''}_${measurement ?? ''}_${index}`

        const isActive = index === superPosIndex
        const positionOffset = Math.abs(index - superPosIndex)
        const opacity = Math.max(0, 1 - Math.abs(superPosIndex - index) * 0.3)

        return (
            <Box
                key={key}
                sx={{
                    opacity: isActive ? 1 : opacity,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    paddingLeft: 'calc(var(--unit)*2)',
                    transition: 'all 0.5s',
                    backfaceVisibility: 'hidden',
                    transformStyle: 'preserve-3d',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 'var(--unit)',
                    pointerEvents: isActive ? 'auto' : 'none',
                    zIndex: superpositions.length - index,
                    transform: isActive
                        ? 'translateZ(0) scale(1)'
                        : `translateZ(-${positionOffset * 22}px) translateY(-${positionOffset * 6}px) scale(${1 - positionOffset * 0.03})`
                }}>
                {nodes.map(x => renderNode(x))}
            </Box>
        )
    }

    return (
        <Box
            key={depth}
            style={{ display: 'flex', flexDirection: 'row' }}>
            <Box style={{
                width: 'calc(var(--unit) * 4)',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {hasSuperpositions && (
                    <ButtonBase onClick={() => setSuperPosIndex(v => (v + 1) % superpositions.length)}>
                        <ChevronLeftRounded style={{ transform: 'rotateZ(90deg)' }} />
                    </ButtonBase>
                )}
                <Typography>{depth + 1}</Typography>
                {hasSuperpositions && (
                    <ButtonBase onClick={() => setSuperPosIndex(v => (v - 1 + superpositions.length) % superpositions.length)}>
                        <ChevronRightRounded style={{ transform: 'rotateZ(90deg)' }} />
                    </ButtonBase>
                )}
            </Box>
            <Divider orientation='vertical' />
            <Box sx={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                padding: '1rem',
                minHeight: 80,
                perspective: '1500px',
                transformStyle: 'preserve-3d'
            }}>
                {superpositions.map((x, i) => renderSuperposition(x, i))}
            </Box>
        </Box>
    )
}

export default QuantumTimelineUI
